import React, { useState, useEffect, useMemo } from 'react';
import CountryFlag from 'react-country-flag';
import { getCountryName } from './countryUtils';

const PAGE_SIZE = 50;
const FIRST_YEAR = 1993; // UFC 1
const COLOR_A = '#4ECDC4';
const COLOR_B = '#FF6B6B';
const COLOR_DRAW = '#777';

// results.db stores all of Russia (North Caucasus included) under 'RU', so don't
// use the "w/o North Caucasus" label that the rankings tables need.
const displayName = (code) => (code === 'RU' ? 'Russia' : getCountryName(code));
const flagCode = (code) => (code === 'UK' ? 'GB' : code);

const selectStyle = {
    padding: '8px 10px',
    borderRadius: 4,
    border: '1px solid #888',
    background: '#1a1a1a',
    color: '#ddd',
    fontSize: '1em',
    minWidth: 220
};

// Who won a fight, from country A's point of view: 'a' | 'b' | 'draw' | 'nc' | null
const getResult = (fight) => {
    if (fight.fight_winner === fight.fighter_a) return 'a';
    if (fight.fight_winner === fight.fighter_b) return 'b';
    if (fight.fight_winner === 'DRAW') return 'draw';
    if (fight.fight_winner === 'NC') return 'nc';
    return null;
};

function CountryPicker({ label, value, options, onChange }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd', gap: 6 }}>
            <span style={{ fontWeight: 500 }}>{label}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {value && <CountryFlag countryCode={flagCode(value)} svg style={{ width: '1.6em', height: '1.6em' }} />}
                <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
                    {options.map(o => (
                        <option key={o.country} value={o.country}>{displayName(o.country)} ({o.fights})</option>
                    ))}
                </select>
            </span>
        </label>
    );
}

function CountryMatchups() {
    const apiBase = process.env.REACT_APP_API_URL || '';
    const [countries, setCountries] = useState([]);
    const [countryA, setCountryA] = useState('US');
    const [countryB, setCountryB] = useState('BR');
    const [allFights, setAllFights] = useState([]);
    // Inclusive year range ('' = unbounded); kept when the country pair changes
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // Load the pickable countries once, alphabetically by display name
    useEffect(() => {
        let cancelled = false;
        fetch(`${apiBase}/api/matchup-countries`)
            .then(res => res.json())
            .then(data => {
                if (cancelled || !Array.isArray(data)) return;
                setCountries(data.sort((x, y) => displayName(x.country).localeCompare(displayName(y.country))));
            })
            .catch(() => { if (!cancelled) setError('Could not load the country list'); });
        return () => { cancelled = true; };
    }, [apiBase]);

    // Load the fights whenever the pair changes (ignoring stale responses)
    useEffect(() => {
        if (!countryA || !countryB) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        setVisibleCount(PAGE_SIZE);
        fetch(`${apiBase}/api/country-matchups?a=${encodeURIComponent(countryA)}&b=${encodeURIComponent(countryB)}`)
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (cancelled) return;
                if (ok && Array.isArray(data)) {
                    setAllFights(data);
                } else {
                    setAllFights([]);
                    setError(data.error || 'Error fetching fights');
                }
            })
            .catch(() => { if (!cancelled) { setAllFights([]); setError('Server error'); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [apiBase, countryA, countryB]);

    const sameCountry = countryA === countryB;

    const fights = useMemo(() => {
        const from = yearFrom === '' ? -Infinity : Number(yearFrom);
        const to = yearTo === '' ? Infinity : Number(yearTo);
        if (from === -Infinity && to === Infinity) return allFights;
        return allFights.filter(f => {
            const year = Number(String(f.event_date).slice(0, 4));
            return year >= from && year <= to;
        });
    }, [allFights, yearFrom, yearTo]);

    // Selectable years: from the first UFC event to the current year, newest first
    const yearOptions = useMemo(() => {
        const last = new Date().getFullYear();
        return Array.from({ length: last - FIRST_YEAR + 1 }, (_, i) => last - i);
    }, []);

    // Keep the range valid: moving one end past the other drags the other end along
    const changeYearFrom = (value) => {
        setYearFrom(value);
        if (value !== '' && yearTo !== '' && Number(value) > Number(yearTo)) setYearTo(value);
        setVisibleCount(PAGE_SIZE);
    };
    const changeYearTo = (value) => {
        setYearTo(value);
        if (value !== '' && yearFrom !== '' && Number(value) < Number(yearFrom)) setYearFrom(value);
        setVisibleCount(PAGE_SIZE);
    };
    const clearYears = () => { setYearFrom(''); setYearTo(''); setVisibleCount(PAGE_SIZE); };
    const yearFilterActive = yearFrom !== '' || yearTo !== '';

    const tally = useMemo(() => {
        const t = { a: 0, b: 0, draw: 0, nc: 0 };
        fights.forEach(f => {
            const r = getResult(f);
            if (r) t[r] += 1;
        });
        return t;
    }, [fights]);

    const swap = () => { setCountryA(countryB); setCountryB(countryA); };

    const decided = tally.a + tally.b;
    const pctA = decided > 0 ? (tally.a / decided) * 100 : 0;

    const thStyle = { padding: '8px', textAlign: 'left', color: '#ddd', fontWeight: 500, borderBottom: '1px solid #555', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '8px', color: '#bbb', borderBottom: '1px solid #2c2c2c' };

    const renderFighter = (name, isWinner, color) => (
        <span style={{ color: isWinner ? color : '#bbb', fontWeight: isWinner ? 700 : 400 }}>
            {name}{isWinner && ' ✓'}
        </span>
    );

    return (
        <div style={{ marginTop: '4em', paddingTop: '2em', borderTop: '1px solid #555' }}>
            <h1 style={{ textAlign: 'center', marginTop: '1.2em', marginBottom: '0.2em', fontWeight: 500, fontSize: '2.2em', color: '#fff' }}>Country vs. Country</h1>
            <div style={{ textAlign: 'center', marginBottom: '2.5em', fontSize: '1.15em', color: '#aaa', fontWeight: 500 }}>
                Every UFC fight between fighters from two countries. Pick the same country twice to list fights between two of its own fighters.
            </div>

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2em', boxSizing: 'border-box' }}>
                {/* Pickers */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'center', gap: '1.5em', marginBottom: '2em' }}>
                    <CountryPicker label="Country A" value={countryA} options={countries} onChange={setCountryA} />
                    <button
                        onClick={swap}
                        title="Swap countries"
                        style={{ height: 38, padding: '0 14px', fontSize: '1.1em', borderRadius: 6, border: '1px solid #888', background: '#232323', color: '#ddd', cursor: 'pointer' }}
                    >⇄</button>
                    <CountryPicker label="Country B" value={countryB} options={countries} onChange={setCountryB} />
                </div>

                {/* Time filter */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'center', gap: '1em', marginBottom: '2em' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd', gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>From year</span>
                        <select value={yearFrom} onChange={e => changeYearFrom(e.target.value)} style={{ ...selectStyle, minWidth: 120 }}>
                            <option value="">Earliest</option>
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd', gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>To year</span>
                        <select value={yearTo} onChange={e => changeYearTo(e.target.value)} style={{ ...selectStyle, minWidth: 120 }}>
                            <option value="">Latest</option>
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </label>
                    {yearFilterActive && (
                        <button
                            onClick={clearYears}
                            style={{ height: 38, padding: '0 14px', fontSize: '1em', borderRadius: 6, border: '1px solid #888', background: '#232323', color: '#ddd', cursor: 'pointer' }}
                        >Reset</button>
                    )}
                </div>

                {error && (
                    <div style={{ color: '#FF6B6B', textAlign: 'center', padding: 16, backgroundColor: 'rgba(255, 107, 107, 0.1)', borderRadius: 4, border: '1px solid #FF6B6B', marginBottom: '1.5em' }}>
                        {error}
                    </div>
                )}

                {loading && <div style={{ color: '#aaa', textAlign: 'center' }}>Loading...</div>}

                {!loading && !error && countries.length > 0 && (
                    fights.length === 0 ? (
                        <div style={{ color: '#aaa', textAlign: 'center', padding: '2em 0' }}>
                            No UFC fights between {displayName(countryA)} and {displayName(countryB)} fighters
                            {allFights.length > 0 && yearFilterActive ? ' in the selected years.' : '.'}
                        </div>
                    ) : (
                        <>
                            {/* Summary */}
                            <div style={{ background: '#1a1a1a', border: '1px solid #555', borderRadius: 8, padding: 16, marginBottom: '1.5em' }}>
                                <div style={{ textAlign: 'center', color: '#fff', fontSize: '1.1em', marginBottom: sameCountry ? 0 : 12 }}>
                                    <b>{fights.length}</b> fight{fights.length === 1 ? '' : 's'}
                                    {(tally.draw > 0 || tally.nc > 0) && (
                                        <span style={{ color: '#aaa', fontSize: '0.85em' }}>
                                            {' '}({[tally.draw > 0 && `${tally.draw} draw${tally.draw === 1 ? '' : 's'}`, tally.nc > 0 && `${tally.nc} no contest${tally.nc === 1 ? '' : 's'}`].filter(Boolean).join(', ')})
                                        </span>
                                    )}
                                </div>
                                {!sameCountry && (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ddd', marginBottom: 6 }}>
                                            <span style={{ color: COLOR_A, fontWeight: 600 }}>{displayName(countryA)}: {tally.a} win{tally.a === 1 ? '' : 's'}</span>
                                            <span style={{ color: COLOR_B, fontWeight: 600 }}>{displayName(countryB)}: {tally.b} win{tally.b === 1 ? '' : 's'}</span>
                                        </div>
                                        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: COLOR_DRAW }}>
                                            {decided > 0 && (
                                                <>
                                                    <div style={{ width: `${pctA}%`, background: COLOR_A }} />
                                                    <div style={{ width: `${100 - pctA}%`, background: COLOR_B }} />
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Fights table */}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Event</th>
                                            <th style={thStyle}>Weight class</th>
                                            <th style={thStyle}>
                                                <CountryFlag countryCode={flagCode(countryA)} svg style={{ marginRight: 6 }} />
                                                {displayName(countryA)}
                                            </th>
                                            <th style={thStyle}>
                                                <CountryFlag countryCode={flagCode(countryB)} svg style={{ marginRight: 6 }} />
                                                {displayName(countryB)}
                                            </th>
                                            <th style={thStyle}>Result</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Rounds</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fights.slice(0, visibleCount).map((f, i) => {
                                            const result = getResult(f);
                                            const isTitle = f.title_fight === 'undisputed' || f.title_fight === 'interim';
                                            return (
                                                <tr key={`${f.event_name}-${f.fighter_a}-${f.fighter_b}-${i}`}>
                                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{f.event_date}</td>
                                                    <td style={tdStyle}>
                                                        {f.event_name}
                                                        {isTitle && <span title={`${f.title_fight} title fight`} style={{ marginLeft: 6 }}>🏆</span>}
                                                    </td>
                                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{f.fight_weight_class || '—'}</td>
                                                    <td style={tdStyle}>{renderFighter(f.fighter_a, result === 'a', COLOR_A)}</td>
                                                    <td style={tdStyle}>{renderFighter(f.fighter_b, result === 'b', COLOR_B)}</td>
                                                    <td style={tdStyle}>{f.fight_outcome_type || '—'}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{f.fight_rounds != null ? f.fight_rounds : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {visibleCount < fights.length && (
                                <div style={{ textAlign: 'center', marginTop: '1.2em' }}>
                                    <button
                                        onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                                        style={{ padding: '8px 16px', fontSize: '1em', fontWeight: 600, borderRadius: 6, border: '1px solid #888', background: '#232323', color: '#ddd', cursor: 'pointer' }}
                                    >
                                        Show more ({fights.length - visibleCount} remaining)
                                    </button>
                                </div>
                            )}
                        </>
                    )
                )}
            </div>
        </div>
    );
}

export default CountryMatchups;
